// src/pages/expenses/ExpensesPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Plus,
  Search,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  XCircle,
  Receipt,
  BarChart3,
  Wallet,
  PieChart as PieIcon,
  Filter,
  Download,
  Settings2,
  Pencil,
  Trash2,
} from "lucide-react";

import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart as RechartsPie,
  Pie,
  Cell,
} from "recharts";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";

import { api, apiFetch } from "@/lib/api";
import { RowActionMenu } from "@/components/RowActionMenu";
import { helper } from "@/lib/helper";

// --------------------
// Types
// --------------------
type ExpenseStatus = "approved" | "pending" | "rejected" | "reimbursed";

type CursorMeta = {
  per_page?: number;
  next_cursor: string | null;
  prev_cursor: string | null;
  has_more: boolean;
};

type ExpenseDTO = {
  id: string;
  expense_date: string; // Y-m-d
  description: string;
  category_id: number | string;
  chart_of_account_id: number | string;
  accountName: string;
  categoryName: string;
  branch: number | string;
  branchName: string;
  vendor: string;
  status: ExpenseStatus;
  amount: number;
  submittedBy: string;
  approvedBy?: string | null;
  notes?: string | null;
  created_at?: string | null;
};

type ExpenseListResponse = {
  data: ExpenseDTO[];
  meta: CursorMeta;
  anchor_id?: string | number | null;
};

type ExpenseSummaryDTO = {
  totalBudget: number;
  totalSpent: number;
  pendingApproval: number;
  pendingAmount: number;
  thisMonth: number;
  lastMonth: number;
};

type CategoryBudgetDTO = {
  category_id: string; // backend key for budgets
  label: string;
  color: string;
  budget: number;
  spent: number;
};

type CategoryBudgetsResponse = { data: CategoryBudgetDTO[] };

type TrendsDynamicResponse = {
  data: {
    series: { key: string; label: string; color: string }[];
    points: { month: string; values: Record<string, number> }[];
  };
};

type BranchOption = { id: number; name: string };
type CategoryOption = { id: number; name: string; color?: string | null; type?: string };
type AccountOption = { id: number; code: string; name: string };

// --------------------
// Helpers
// --------------------
const API_PATH = "/actions/expenses";

function cx(...cls: Array<string | false | null | undefined>) {
  return cls.filter(Boolean).join(" ");
}

function formatCurrency(n: number) {
  const x = Number(n || 0);
  return x.toLocaleString("en-KE");
}

function yyyymm(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

const statusConfig: Record<ExpenseStatus, { label: string; icon: React.ElementType; className: string }> = {
  approved: { label: "Approved", icon: CheckCircle2, className: "bg-primary/10 text-primary" },
  pending: { label: "Pending", icon: Clock, className: "bg-chart-4/15 text-[hsl(43,96%,40%)]" },
  rejected: { label: "Rejected", icon: XCircle, className: "bg-destructive/10 text-destructive" },
  reimbursed: { label: "Reimbursed", icon: Receipt, className: "bg-chart-2/15 text-[hsl(199,89%,48%)]" },
};

export default function ExpensesPage() {
  const [activeTab, setActiveTab] = useState("expenses");

  // List Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [branchFilter, setBranchFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all"); // category_id
  const [accountFilter, setAccountFilter] = useState<string>("all"); // chart_of_account_id

  // Cursor pagination
  const [meta, setMeta] = useState<CursorMeta>({
    per_page: 10,
    next_cursor: null,
    prev_cursor: null,
    has_more: false,
  });
  const [cursor, setCursor] = useState<string | null>(null);
  const [anchorId, setAnchorId] = useState<string | number | null>(null);

  // List data
  const [items, setItems] = useState<ExpenseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Report filters
  const [reportMonth, setReportMonth] = useState<string>(yyyymm(new Date()));
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);

  // Reporting data
  const [summary, setSummary] = useState<ExpenseSummaryDTO>({
    totalBudget: 0,
    totalSpent: 0,
    pendingApproval: 0,
    pendingAmount: 0,
    thisMonth: 0,
    lastMonth: 0,
  });

  const [categoryBudgets, setCategoryBudgets] = useState<CategoryBudgetDTO[]>([]);

  // Dynamic trends
  const [trendSeries, setTrendSeries] = useState<{ key: string; label: string; color: string }[]>([]);
  const [trendPoints, setTrendPoints] = useState<{ month: string; values: Record<string, number> }[]>([]);

  // dialogs
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [editExpenseOpen, setEditExpenseOpen] = useState(false);
  const [setBudgetOpen, setSetBudgetOpen] = useState(false);

  // --------------------
  // META QUERIES
  // --------------------
  const branchesQ = useQuery({
    queryKey: ["expense_meta_branches"],
    queryFn: () => apiFetch<{ data: BranchOption[] }>(`${API_PATH}/branches`),
    staleTime: 60_000,
  });

  const categoriesQ = useQuery({
    queryKey: ["expense_meta_categories"],
    queryFn: () => apiFetch<{ data: CategoryOption[] }>(`${API_PATH}/categories?type=Expenses`),
    staleTime: 60_000,
  });

  // accounts depend on (selected category + optional branch)
  const accountsQ = useQuery({
    queryKey: ["expense_meta_accounts", categoryFilter, branchFilter],
    enabled: categoryFilter !== "all" && categoryFilter !== "",
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("category_id", categoryFilter);
      if (branchFilter !== "all") params.set("branch", branchFilter);
      return apiFetch<{ data: AccountOption[] }>(`${API_PATH}/accounts?${params.toString()}`);
    },
    staleTime: 30_000,
  });

  // Reset account filter when category changes
  useEffect(() => {
    setAccountFilter("all");
  }, [categoryFilter]);

  // --------------------
  // FORM (Add/Edit)
  // --------------------
  const emptyForm = {
    id: "",
    expense_date: "",
    branch: "",
    category_id: "",
    chart_of_account_id: "",
    description: "",
    vendor: "",
    amount: "",
    notes: "",
  };

  const [form, setForm] = useState<typeof emptyForm>(emptyForm);

  // budgets edit (dialog)
  const [editingBudgets, setEditingBudgets] = useState<Record<string, string>>({});

  // Derived
  const pendingExpenses = useMemo(() => items.filter((e) => e.status === "pending"), [items]);
  const totalBudget = useMemo(() => categoryBudgets.reduce((s, b) => s + (b.budget || 0), 0), [categoryBudgets]);
  const totalSpent = useMemo(() => categoryBudgets.reduce((s, b) => s + (b.spent || 0), 0), [categoryBudgets]);

  const pieData = useMemo(
    () =>
      categoryBudgets.map((b) => ({
        name: b.label,
        value: b.spent,
        color: b.color,
      })),
    [categoryBudgets]
  );

  const trendChartData = useMemo(() => {
    return (trendPoints || []).map((p) => {
      const row: any = { month: p.month };
      for (const s of trendSeries) row[s.key] = p.values?.[s.key] ?? 0;
      return row;
    });
  }, [trendPoints, trendSeries]);

  // ---------------------------
  // LIST fetch
  // ---------------------------
  const fetchExpenses = async (opts?: { cursor?: string | null }) => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.set("per_page", String(meta.per_page || 10));
    if (opts?.cursor) params.set("cursor", opts.cursor);

    if (searchQuery) params.set("q", searchQuery);
    params.set("status", statusFilter || "all");

    if (branchFilter !== "all") params.set("branch", branchFilter);
    if (categoryFilter !== "all") params.set("category_id", categoryFilter);
    if (accountFilter !== "all") params.set("chart_of_account_id", accountFilter);

    if (anchorId != null) params.set("anchor_id", String(anchorId));

    try {
      const json = await apiFetch<ExpenseListResponse>(`${API_PATH}?${params.toString()}`);
      setItems(json.data ?? []);
      setMeta((prev) => ({
        ...prev,
        per_page: json.meta?.per_page ?? prev.per_page ?? 10,
        next_cursor: json.meta?.next_cursor ?? null,
        prev_cursor: json.meta?.prev_cursor ?? null,
        has_more: json.meta?.has_more ?? false,
      }));
      setCursor(opts?.cursor ?? null);
      setAnchorId((prev) => (prev == null ? json.anchor_id ?? null : prev));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load expenses");
      setItems([]);
      setMeta((prev) => ({ ...prev, next_cursor: null, prev_cursor: null, has_more: false }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses({ cursor: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, statusFilter, branchFilter, categoryFilter, accountFilter]);

  const goNext = () => meta.next_cursor && !loading && fetchExpenses({ cursor: meta.next_cursor });
  const goPrev = () => meta.prev_cursor && !loading && fetchExpenses({ cursor: meta.prev_cursor });

  // ---------------------------
  // REPORTS fetch
  // ---------------------------
  const fetchReports = async () => {
    setReportsLoading(true);
    setReportsError(null);

    try {
      const [sum, cats, tr] = await Promise.all([
        apiFetch<{ data: ExpenseSummaryDTO }>(`${API_PATH}/reports/summary?month=${encodeURIComponent(reportMonth)}`),
        apiFetch<CategoryBudgetsResponse>(`${API_PATH}/reports/categories?month=${encodeURIComponent(reportMonth)}`),
        apiFetch<TrendsDynamicResponse>(`${API_PATH}/reports/trends?months=12`),
      ]);

      setSummary(sum.data);
      setCategoryBudgets(cats.data ?? []);
      setTrendSeries(tr.data.series ?? []);
      setTrendPoints(tr.data.points ?? []);
    } catch (e: any) {
      setReportsError(e?.message ?? "Failed to load reports");
      setSummary({
        totalBudget: 0,
        totalSpent: 0,
        pendingApproval: 0,
        pendingAmount: 0,
        thisMonth: 0,
        lastMonth: 0,
      });
      setCategoryBudgets([]);
      setTrendSeries([]);
      setTrendPoints([]);
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportMonth]);

  // ---------------------------
  // CRUD
  // ---------------------------
  const openAdd = () => {
    setError(null);
    setForm({ ...emptyForm, expense_date: new Date().toISOString().slice(0, 10) });
    setAddExpenseOpen(true);
  };

  const openEdit = (exp: ExpenseDTO) => {
    setError(null);
    setForm({
      id: exp.id,
      expense_date: exp.expense_date,
      branch: String(exp.branch ?? ""),
      category_id: String(exp.category_id ?? ""),
      chart_of_account_id: String(exp.chart_of_account_id ?? ""),
      description: exp.description,
      vendor: exp.vendor,
      amount: String(exp.amount ?? ""),
      notes: exp.notes ?? "",
    });
    setEditExpenseOpen(true);
  };

  const validateForm = () => {
    if (!form.expense_date) return "Date is required";
    if (!form.branch) return "Branch is required";
    if (!form.category_id) return "Category is required";
    if (!form.chart_of_account_id) return "Account is required";
    if (!form.description.trim()) return "Description is required";
    if (!form.vendor.trim()) return "Vendor is required";
    return null;
  };

  const createExpense = async () => {
    const msg = validateForm();
    if (msg) return setError(msg);

    const payload = {
      expense_date: form.expense_date,
      branch: Number(form.branch),
      category_id: Number(form.category_id),
      chart_of_account_id: Number(form.chart_of_account_id),
      description: form.description,
      vendor: form.vendor,
      amount: Number(form.amount || 0),
      notes: form.notes || null,
    };

    setLoading(true);
    setError(null);
    try {
      await api.post(`${API_PATH}`, payload);
      setAddExpenseOpen(false);
      await fetchExpenses({ cursor: null });
      await fetchReports();
    } catch (e: any) {
      setError(e?.message ?? "Failed to create expense");
    } finally {
      setLoading(false);
    }
  };

  const updateExpense = async () => {
    if (!form.id) return setError("Missing expense id");
    const msg = validateForm();
    if (msg) return setError(msg);

    const payload = {
      expense_date: form.expense_date,
      branch: Number(form.branch),
      category_id: Number(form.category_id),
      chart_of_account_id: Number(form.chart_of_account_id),
      description: form.description,
      vendor: form.vendor,
      amount: Number(form.amount || 0),
      notes: form.notes || null,
    };

    setLoading(true);
    setError(null);
    try {
      await api.post(`${API_PATH}/${form.id}/update`, payload);
      setEditExpenseOpen(false);
      await fetchExpenses({ cursor });
      await fetchReports();
    } catch (e: any) {
      setError(e?.message ?? "Failed to update expense");
    } finally {
      setLoading(false);
    }
  };

  const deleteExpense = async (id: string) => {
    if (!window.confirm("Delete this expense?")) return;

    setLoading(true);
    setError(null);
    try {
      await api.del(`${API_PATH}/${id}`);
      await fetchExpenses({ cursor });
      await fetchReports();
    } catch (e: any) {
      setError(e?.message ?? "Failed to delete expense");
    } finally {
      setLoading(false);
    }
  };

  const approveExpense = async (id: string) => {
    try {
      await api.post(`${API_PATH}/${id}/approve`);
      await fetchExpenses({ cursor });
      await fetchReports();
    } catch { }
  };

  const rejectExpense = async (id: string) => {
    try {
      await api.post(`${API_PATH}/${id}/reject`, { reason: "Not compliant" });
      await fetchExpenses({ cursor });
      await fetchReports();
    } catch { }
  };

  // ---------------------------
  // Budgets
  // ---------------------------
  const openBudgetDialog = () => {
    setError(null);
    setEditingBudgets(Object.fromEntries(categoryBudgets.map((b) => [b.category_id, String(b.budget)])));
    setSetBudgetOpen(true);
  };

  const saveBudgets = async () => {
    const budgetsPayload = categoryBudgets.map((b) => ({
      category_id: b.category_id,
      budget: Number(String(editingBudgets[b.category_id] ?? b.budget).replace(/,/g, "")) || 0,
    }));

    setLoading(true);
    setError(null);
    try {
      await api.put(`${API_PATH}/budgets`, { budgets: budgetsPayload });
      setSetBudgetOpen(false);
      await fetchReports();
    } catch (e: any) {
      setError(e?.message ?? "Failed to save budgets");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------
  // FORM ACCOUNTS
  // ---------------------------
  const formAccountsQ = useQuery({
    queryKey: ["expense_meta_accounts_form", form.category_id, form.branch],
    enabled: !!form.category_id,
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("category_id", String(form.category_id));
      if (form.branch) params.set("branch", String(form.branch));
      return apiFetch<{ data: AccountOption[] }>(`${API_PATH}/accounts?${params.toString()}`);
    },
    staleTime: 30_000,
  });

  // reset form account when category changes
  useEffect(() => {
    setForm((p) => ({ ...p, chart_of_account_id: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.category_id]);

  const [dir, setDir] = useState<"ltr" | "rtl">(() => helper.getDir());
  const isRTL = dir === "rtl";

  useEffect(() => {
    // watch <html dir="..."> changes
    const el = document.documentElement;
    const obs = new MutationObserver(() => setDir(helper.getDir()));
    obs.observe(el, { attributes: true, attributeFilter: ["dir"] });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background" dir={dir}>
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center shrink-0">
                <Wallet className="h-5 w-5 text-primary-foreground" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-foreground tracking-tight truncate">Expenses</h1>
                <p className="text-xs text-muted-foreground truncate">Full Expense Manager</p>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
              <Button variant="outline" size="sm" className="gap-2 w-full sm:w-auto">
                <Download className="h-4 w-4" /> Export
              </Button>

              <Button className="gap-2 w-full sm:w-auto" onClick={openAdd}>
                <Plus className="h-4 w-4" /> Add Expense
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground font-medium">Monthly Budget</p>
                  <p className="text-2xl font-bold text-foreground mt-1">KES {(summary.totalBudget / 1_000_000).toFixed(1)}M</p>
                  <p className="text-xs text-muted-foreground mt-1">Total allocation</p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Wallet className="h-5 w-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground font-medium">Total Spent</p>
                  <p className="text-2xl font-bold text-foreground mt-1">KES {(summary.totalSpent / 1_000_000).toFixed(1)}M</p>
                  <p className="text-xs text-primary mt-1 flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    {summary.totalBudget > 0 ? ((summary.totalSpent / summary.totalBudget) * 100).toFixed(0) : "0"}% of budget
                  </p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <DollarSign className="h-5 w-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground font-medium">Pending Approval</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{summary.pendingApproval}</p>
                  <p className="text-xs text-[hsl(43,96%,40%)] mt-1">KES {formatCurrency(summary.pendingAmount)}</p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-chart-4/15 flex items-center justify-center shrink-0">
                  <Clock className="h-5 w-5 text-[hsl(43,96%,40%)]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground font-medium">This vs Last Month</p>
                  <p className="text-2xl font-bold text-foreground mt-1">
                    {summary.lastMonth > 0 ? `${(((summary.thisMonth - summary.lastMonth) / summary.lastMonth) * 100).toFixed(1)}%` : "—"}
                  </p>
                  <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                    <TrendingDown className="h-3 w-3" /> KES {formatCurrency(summary.thisMonth - summary.lastMonth)} more
                  </p>
                </div>
                <div className="h-11 w-11 rounded-xl bg-accent flex items-center justify-center shrink-0">
                  <BarChart3 className="h-5 w-5 text-accent-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-card border border-border w-full flex flex-wrap justify-start">
            <TabsTrigger value="expenses" className="gap-2">
              <Receipt className="h-4 w-4" />
              Expenses
            </TabsTrigger>
            <TabsTrigger value="budgets" className="gap-2">
              <PieIcon className="h-4 w-4" />
              Budgets
            </TabsTrigger>
            <TabsTrigger value="approvals" className="gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Approvals
            </TabsTrigger>
            <TabsTrigger value="reports" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Reports
            </TabsTrigger>
          </TabsList>

          {/* Expenses Tab */}
          <TabsContent value="expenses" className="space-y-4">
            {/* Filters - responsive grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3">
              <div className="relative lg:col-span-4">
                <Search
                  className={cx(
                    "absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground",
                    isRTL ? "right-3" : "left-3"
                  )}
                />

                <Input
                  placeholder="Search expenses..."
                  className={cx(isRTL ? "pr-9" : "pl-9")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="lg:col-span-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full">
                    <Filter className={cx("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="reimbursed">Reimbursed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="lg:col-span-2">
                <Select value={branchFilter} onValueChange={setBranchFilter}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Branch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {(branchesQ.data?.data ?? []).map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="lg:col-span-2">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {(categoriesQ.data?.data ?? []).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="lg:col-span-2">
                <Select value={accountFilter} onValueChange={setAccountFilter} disabled={categoryFilter === "all" || accountsQ.isLoading}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={categoryFilter === "all" ? "Select category first" : "Account"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Accounts</SelectItem>
                    {(accountsQ.data?.data ?? []).map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.code} • {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between gap-2 lg:col-span-12">
                <div className="text-xs text-muted-foreground">{loading ? "Loading..." : `${items.length} record(s)`}</div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={goPrev} disabled={!meta.prev_cursor || loading}>
                    Prev
                  </Button>
                  <Button variant="outline" size="sm" onClick={goNext} disabled={!meta.next_cursor || loading}>
                    Next
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <Card className="border-destructive/30">
                <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
              </Card>
            )}

            <Card>
              {/* Desktop / iPad table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-end">Amount (KES)</TableHead>
                      <TableHead>Submitted By</TableHead>
                      <TableHead className="text-end">Actions</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={11} className="py-10 text-center text-muted-foreground">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} className="py-10 text-center text-muted-foreground">
                          No expenses found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((exp) => {
                        const sc = statusConfig[exp.status];
                        const Icon = sc.icon;
                        return (
                          <TableRow key={exp.id}>
                            <TableCell className="font-mono text-xs text-muted-foreground">{exp.id}</TableCell>
                            <TableCell className="text-sm">{exp.expense_date}</TableCell>
                            <TableCell className="font-medium max-w-[220px] truncate">{exp.description}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{exp.branchName ?? "—"}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{exp.categoryName ?? "—"}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{exp.accountName ?? "—"}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{exp.vendor}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className={cx("gap-1", sc.className)}>
                                <Icon className="h-3 w-3" />
                                {sc.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-end font-mono text-sm font-medium">{formatCurrency(exp.amount)}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{exp.submittedBy}</TableCell>
                            <TableCell className="text-end">
                              {/* <div className="inline-flex gap-2">
                                <Button variant="outline" size="sm" className="gap-1" onClick={() => openEdit(exp)}>
                                  <Pencil className="h-3.5 w-3.5" /> Edit
                                </Button>
                                <Button variant="outline" size="sm" className="gap-1 text-destructive" onClick={() => deleteExpense(exp.id)}>
                                  <Trash2 className="h-3.5 w-3.5" /> Delete
                                </Button>
                              </div> */}
                              <RowActionMenu
                                isRTL={isRTL}

                                onEdit={() => openEdit(exp)}

                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden p-3 space-y-3">
                {loading ? (
                  <div className="py-10 text-center text-muted-foreground">Loading...</div>
                ) : items.length === 0 ? (
                  <div className="py-10 text-center text-muted-foreground">No expenses found.</div>
                ) : (
                  items.map((exp) => {
                    const sc = statusConfig[exp.status];
                    const Icon = sc.icon;
                    return (
                      <div key={exp.id} className="rounded-xl border border-border bg-background p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold truncate">{exp.description}</div>
                            <div className="text-xs text-muted-foreground mt-1 space-y-1">
                              <div className="flex flex-wrap gap-x-2 gap-y-1">
                                <span>{exp.expense_date || "—"}</span>
                                <span>•</span>
                                <span className="truncate">{exp.branchName ?? "—"}</span>
                              </div>
                              <div className="truncate">{exp.categoryName ?? "—"}</div>
                              <div className="truncate">{exp.accountName ?? "—"}</div>
                              <div className="truncate">Vendor: {exp.vendor ?? "—"}</div>
                              <div className="truncate">By: {exp.submittedBy ?? "—"}</div>
                            </div>
                            
                          </div>

                          <div className="shrink-0 text-end">
                            <div className="font-mono font-bold">KES {formatCurrency(exp.amount)}</div>
                            <Badge variant="secondary" className={cx("mt-2 gap-1", sc.className)}>
                              <Icon className="h-3 w-3" />
                              {sc.label}
                            </Badge>
                            <RowActionMenu
                                  isRTL={isRTL}
                                 
                                  onEdit={() => openEdit(exp)}
                                />
                          </div>
                          
                        </div>

                        {/* <div className="flex gap-2 mt-3">
                          <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => openEdit(exp)}>
                            <Pencil className="h-3.5 w-3.5" /> Edit
                          </Button>
                          <Button variant="outline" size="sm" className="flex-1 gap-1 text-destructive" onClick={() => deleteExpense(exp.id)}>
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </Button>
                        </div> */}
                      </div>
                    );
                  })
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Budgets Tab */}
          <TabsContent value="budgets" className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-medium text-muted-foreground">
                  Total Budget: <span className="text-foreground font-bold">KES {formatCurrency(totalBudget)}</span>
                </h2>
              </div>
              <Button variant="outline" size="sm" className="gap-2 w-full sm:w-auto" onClick={openBudgetDialog}>
                <Settings2 className="h-4 w-4" /> Set Budgets
              </Button>
            </div>

            <Dialog open={setBudgetOpen} onOpenChange={setSetBudgetOpen}>
              <DialogContent className="w-[95vw] sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Set Category Budgets</DialogTitle>
                </DialogHeader>

                <div className="space-y-3 pt-2">
                  {categoryBudgets.map((b) => (
                    <div key={b.category_id} className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-sm shrink-0" style={{ backgroundColor: b.color }} />
                      <Label className="flex-1 text-sm font-medium">{b.label}</Label>
                      <div className="relative w-[180px]">
                        <span
                          className={cx(
                            "absolute top-1/2 -translate-y-1/2 text-xs text-muted-foreground",
                            isRTL ? "right-3" : "left-3"
                          )}
                        >
                          KES
                        </span>

                        <Input
                          className={cx(
                            "font-mono text-end",
                            isRTL ? "pr-11" : "pl-11"
                          )}
                          value={editingBudgets[b.category_id] || ""}
                          onChange={(e) => setEditingBudgets((prev) => ({ ...prev, [b.category_id]: e.target.value }))}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 pt-3">
                  <Button variant="outline" className="flex-1" onClick={() => setSetBudgetOpen(false)}>
                    Cancel
                  </Button>
                  <Button className="flex-1" onClick={saveBudgets} disabled={loading}>
                    Save Budgets
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Category Budget Utilisation</CardTitle>
                    <CardDescription>Monthly budget vs actual spend per category</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {categoryBudgets.map((b) => {
                      const pct = b.budget > 0 ? Math.round((b.spent / b.budget) * 100) : 0;
                      const isOver = pct > 90;
                      return (
                        <div key={b.category_id} className="space-y-2">
                          <div className="flex items-center justify-between text-sm gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="h-3 w-3 rounded-sm shrink-0" style={{ backgroundColor: b.color }} />
                              <span className="font-medium text-foreground truncate">{b.label}</span>
                            </div>
                            <div className="flex items-center gap-3 text-muted-foreground shrink-0">
                              <span className="font-mono">KES {formatCurrency(b.spent)}</span>
                              <span>/</span>
                              <span className="font-mono">KES {formatCurrency(b.budget)}</span>
                              <span className={cx("font-bold", isOver ? "text-destructive" : "text-primary")}>{pct}%</span>
                            </div>
                          </div>
                          <Progress value={Math.min(pct, 100)} className="h-2" />
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Spend Distribution</CardTitle>
                  <CardDescription>By category</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" nameKey="name">
                          {pieData.map((entry, i) => (
                            <Cell key={`cell-${i}`} fill={entry.color} stroke="transparent" />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => `KES ${formatCurrency(value)}`} />
                        <Legend verticalAlign="bottom" />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Approvals Tab */}
          <TabsContent value="approvals" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Pending Approvals</CardTitle>
                <CardDescription>
                  {pendingExpenses.length} expense{pendingExpenses.length !== 1 ? "s" : ""} awaiting review
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-3">
                {loading ? (
                  <div className="py-10 text-center text-muted-foreground">Loading...</div>
                ) : pendingExpenses.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    <p>All caught up! No pending approvals.</p>
                  </div>
                ) : (
                  pendingExpenses.map((exp) => (
                    <div
                      key={exp.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-start gap-4 min-w-0">
                        <div className="h-10 w-10 rounded-xl bg-chart-4/15 flex items-center justify-center shrink-0">
                          <Clock className="h-5 w-5 text-[hsl(43,96%,40%)]" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">{exp.description}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {exp.submittedBy} · {exp.categoryName ?? "—"} · {exp.expense_date}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between sm:justify-end gap-3">
                        <span className="text-lg font-bold font-mono text-foreground">KES {formatCurrency(exp.amount)}</span>
                        <div className="flex gap-2">
                          <Button size="sm" className="gap-1" onClick={() => approveExpense(exp.id)}>
                            <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1 text-destructive hover:text-destructive" onClick={() => rejectExpense(exp.id)}>
                            <XCircle className="h-3.5 w-3.5" /> Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Report Month</Label>
                <Input type="month" value={reportMonth} onChange={(e) => setReportMonth(e.target.value)} className="w-full sm:w-[220px]" />
              </div>

              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchReports} disabled={reportsLoading}>
                  Refresh
                </Button>
                {reportsError && <span className="text-sm text-destructive self-center">{reportsError}</span>}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Monthly Expense Trends</CardTitle>
                  <CardDescription>Dynamic categories over 12 months (from backend)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trendChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => `${(Number(v) / 1_000_000).toFixed(1)}M`} />
                        <Tooltip formatter={(v: number) => `KES ${formatCurrency(Number(v))}`} />
                        <Legend />
                        {trendSeries.slice(0, 6).map((s) => (
                          <Area key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} fill={s.color} fillOpacity={0.1} />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Operational Costs Breakdown</CardTitle>
                  <CardDescription>Dynamic categories as bars (top 6)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={trendChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(v) => `${(Number(v) / 1_000).toFixed(0)}K`} />
                        <Tooltip formatter={(v: number) => `KES ${formatCurrency(Number(v))}`} />
                        <Legend />
                        {trendSeries.slice(0, 6).map((s) => (
                          <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* ADD Dialog */}
      <Dialog open={addExpenseOpen} onOpenChange={setAddExpenseOpen}>
        <DialogContent className="w-[95vw] sm:max-w-[580px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Expense</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {error && (
              <Card className="border-destructive/30">
                <CardContent className="p-3 text-sm text-destructive">{error}</CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => setForm((p) => ({ ...p, expense_date: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Branch</Label>
                <Select value={form.branch} onValueChange={(v) => setForm((p) => ({ ...p, branch: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {(branchesQ.data?.data ?? []).map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm((p) => ({ ...p, category_id: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {(categoriesQ.data?.data ?? []).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Account</Label>
                <Select
                  value={form.chart_of_account_id}
                  onValueChange={(v) => setForm((p) => ({ ...p, chart_of_account_id: v }))}
                  disabled={!form.category_id || formAccountsQ.isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={!form.category_id ? "Select category first" : "Select account"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(formAccountsQ.data?.data ?? []).map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.code} • {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (KES)</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input value={form.vendor} onChange={(e) => setForm((p) => ({ ...p, vendor: e.target.value }))} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setAddExpenseOpen(false)}>
                Cancel
              </Button>
              <Button className="flex-1 gap-2" onClick={createExpense} disabled={loading}>
                <Receipt className="h-4 w-4" /> Submit Expense
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* EDIT Dialog */}
      <Dialog open={editExpenseOpen} onOpenChange={setEditExpenseOpen}>
        <DialogContent className="w-[95vw] sm:max-w-[580px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Expense</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {error && (
              <Card className="border-destructive/30">
                <CardContent className="p-3 text-sm text-destructive">{error}</CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => setForm((p) => ({ ...p, expense_date: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Branch</Label>
                <Select value={form.branch} onValueChange={(v) => setForm((p) => ({ ...p, branch: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {(branchesQ.data?.data ?? []).map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm((p) => ({ ...p, category_id: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {(categoriesQ.data?.data ?? []).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Account</Label>
                <Select
                  value={form.chart_of_account_id}
                  onValueChange={(v) => setForm((p) => ({ ...p, chart_of_account_id: v }))}
                  disabled={!form.category_id || formAccountsQ.isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={!form.category_id ? "Select category first" : "Select account"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(formAccountsQ.data?.data ?? []).map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.code} • {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (KES)</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input value={form.vendor} onChange={(e) => setForm((p) => ({ ...p, vendor: e.target.value }))} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditExpenseOpen(false)}>
                Cancel
              </Button>
              <Button className="flex-1 gap-2" onClick={updateExpense} disabled={loading}>
                <Pencil className="h-4 w-4" /> Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}